import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search as SearchIcon, MapPin, ArrowRight, Crosshair, Sparkles, ChevronRight, Star } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import AppLauncher from "@/pages/AppLauncher";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import * as LucideIcons from "lucide-react";
import AppIcon, { canonicalIconName } from "@/components/AppIcon";
import { ContextAssist } from "@/components/ContextAssist";
import MyDayCard from "@/components/MyDayCard";
import CoverageChip from "@/components/CoverageChip";
import { WeatherCard } from "@/components/weather/WeatherWidgets";
import NearbyHomeWidget from "@/components/NearbyHomeWidget";
import { useI18n } from "@/context/I18nContext";

// Curated public-launcher grid — same look/feel as the logged-in AppLauncher, but
// every tile points at a category-scoped Search URL so anonymous visitors can browse
// freely without an account. When they tap sign-in they land in the same launcher.
const PUBLIC_GROUPS = [
  {
    title: "Everyday needs",
    apps: [
      { id: "service",      label: "Services",     icon: "Wrench",         grad: "from-slate-600 to-slate-800",         to: "/search?category=service" },
      { id: "product",      label: "Shopping",     icon: "ShoppingBag",    grad: "from-orange-500 to-red-600",          to: "/search?category=product" },
      { id: "restaurant",   label: "Food",         icon: "UtensilsCrossed", grad: "from-red-500 to-rose-600",           to: "/search?category=restaurant" },
      { id: "hotel",        label: "Hotels",       icon: "BedDouble",      grad: "from-teal-500 to-emerald-600",        to: "/search?category=hotel" },
      { id: "vacation",     label: "Vacation",     icon: "Palmtree",       grad: "from-sky-500 to-cyan-600",            to: "/search?category=vacation" },
      { id: "store",        label: "Stores",       icon: "Store",          grad: "from-amber-500 to-orange-600",        to: "/search?category=store" },
    ],
  },
  {
    title: "Life & Family",
    apps: [
      { id: "job",          label: "Jobs",         icon: "Briefcase",      grad: "from-blue-600 to-indigo-700",         to: "/search?category=job" },
      { id: "property",     label: "Property",     icon: "Home",           grad: "from-emerald-600 to-teal-700",        to: "/search?category=property" },
      { id: "matrimonial",  label: "Matrimonial",  icon: "Heart",          grad: "from-rose-500 to-pink-600",           to: "/search?category=matrimonial" },
      { id: "doctor",       label: "Doctors",      icon: "Stethoscope",    grad: "from-emerald-500 to-green-700",       to: "/search?category=doctor" },
      { id: "hospital",     label: "Hospitals",    icon: "Building2",      grad: "from-red-600 to-rose-700",            to: "/search?category=hospital" },
      { id: "school",       label: "Schools",      icon: "GraduationCap",  grad: "from-indigo-500 to-blue-700",         to: "/search?category=school" },
      { id: "tutor",        label: "Tutors",       icon: "PencilRuler",    grad: "from-violet-500 to-purple-700",       to: "/search?category=tutor" },
      { id: "baby_care",    label: "Baby Care",    icon: "Baby",           grad: "from-pink-400 to-rose-500",           to: "/search?category=baby_care" },
      { id: "elder_care",   label: "Elder Care",   icon: "HeartHandshake", grad: "from-amber-600 to-orange-700",        to: "/search?category=elder_care" },
      { id: "pet_care",     label: "Pet Care",     icon: "PawPrint",       grad: "from-yellow-600 to-amber-700",        to: "/search?category=pet_care" },
    ],
  },
  {
    title: "Travel & Fun",
    apps: [
      { id: "cab",          label: "Cabs",         icon: "Car",            grad: "from-yellow-500 to-amber-600",        to: "/search?category=cab" },
      { id: "flight",       label: "Flights",     icon: "Plane",          grad: "from-sky-600 to-blue-700",            to: "/flights" },
      { id: "train",        label: "Trains",       icon: "TrainFront",     grad: "from-blue-700 to-indigo-800",         to: "/search?category=train" },
      { id: "bus",          label: "Buses",        icon: "Bus",            grad: "from-orange-600 to-red-700",          to: "/search?category=bus" },
      { id: "marriage_hall",label: "Weddings",     icon: "Church",         grad: "from-rose-600 to-pink-700",           to: "/search?category=marriage_hall" },
      { id: "party_hall",   label: "Party Halls",  icon: "PartyPopper",    grad: "from-purple-600 to-fuchsia-700",      to: "/search?category=party_hall" },
      { id: "movie",        label: "Movies",       icon: "Film",           grad: "from-red-700 to-rose-800",            to: "/search?category=movie" },
      { id: "gym",          label: "Gym",          icon: "Dumbbell",       grad: "from-stone-700 to-black",             to: "/search?category=gym" },
      { id: "salon",        label: "Salon",        icon: "Scissors",       grad: "from-purple-500 to-fuchsia-600",      to: "/search?category=salon" },
    ],
  },
  {
    title: "Explore",
    apps: [
      { id: "visa",         label: "Visa Apply",   icon: "Globe",          grad: "from-emerald-600 to-teal-900",        to: "/visa" },
      { id: "lab",          label: "AI Lab",       icon: "Beaker",         grad: "from-fuchsia-600 to-purple-800",      to: "/lab" },
      { id: "civic",        label: "Govt Portals", icon: "Landmark",       grad: "from-blue-700 to-indigo-900",         to: "/civic-portals" },
      { id: "learn",        label: "Learn",        icon: "PlayCircle",     grad: "from-purple-500 to-indigo-700",       to: "/learn" },
      { id: "brands",       label: "Brands",       icon: "Star",           grad: "from-yellow-500 to-amber-600",        to: "/brands" },
      { id: "prices",       label: "Prices",       icon: "LineChart",      grad: "from-lime-600 to-emerald-700",        to: "/prices" },
      { id: "post",         label: "Post Free Ad", icon: "PlusCircle",     grad: "from-[#2D5A27] to-emerald-800",       to: "/post" },
      { id: "list-biz",     label: "List Biz",     icon: "Store",          grad: "from-[#E05E36] to-red-700",           to: "/list-my-business" },
      { id: "emergency",    label: "Emergency",    icon: "Siren",          grad: "from-red-600 to-red-900",             to: "/emergency" },
    ],
  },
];

// Grab lucide icon by string name, safely fallback to a star.
const iconByName = (name) => LucideIcons[name] || LucideIcons.Star;

export default function Home() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [trending, setTrending] = useState([]);
  const navigate = useNavigate();
  const { t } = useI18n();
  const { loc, detect, loading: locLoading, state: locState, age_label: locAge } = useUserLocation();
  const city = loc.source !== "default" ? loc.city : "";  // never show the Mumbai placeholder as "your" city

  useSeo({
    title: city
      ? `Find services, jobs, homes, doctors & more near ${city}`
      : "Find2Near — Tell us what you need. We find it near you.",
    description: `Find2Near is the universal nearby finder. Search for a doctor, plumber, 2BHK, hotel, job, school or shop and see verified options${city ? ` in ${city}` : " near you"} — with live location, secure messaging and payments.`,
    canonical: "/",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "find2near",
      url: typeof window !== "undefined" ? window.location.origin : "https://find2near.com",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${typeof window !== "undefined" ? window.location.origin : "https://find2near.com"}/search?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
  });

  // Logged-in users get the private launcher. Everyone else gets the public one below.
  useEffect(() => {
    if (user) return;
    (async () => {
      try {
        const r = await api.get("/listings", { params: { limit: 6, lat: loc.lat, lng: loc.lng } });
        setTrending(r.data);
      } catch {}
    })();
  }, [loc.lat, loc.lng, user]);

  if (user) return <AppLauncher />;

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] relative overflow-hidden bg-white"
      style={{
        background:
          "radial-gradient(1200px 700px at 20% -10%, rgba(224,94,54,0.10), transparent 60%)," +
          "radial-gradient(1000px 600px at 90% 10%, rgba(45,90,39,0.12), transparent 60%)," +
          "linear-gradient(180deg, #ffffff 0%, #fafaf7 60%, #f5f5ef 100%)",
      }}
      data-testid="home-public"
    >
      {/* subtle grain overlay so tiles pop */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      {/* Mobile floating Live World pill — desktop uses the CTA in the hero */}
      <Link
        to="/live-world"
        className="sm:hidden fixed top-20 right-3 z-30 rounded-full bg-gradient-to-r from-[#E05E36] to-[#c94e2a] text-white text-xs font-black px-3 py-1.5 shadow-lg inline-flex items-center gap-1"
        data-testid="home-liveworld-fab"
      >🌍 {t("home.live_world")}</Link>

      <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-8 pb-24">
        {/* Header — tagline + location + sign-in CTA */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 rounded-full bg-stone-900/5 backdrop-blur border border-stone-900/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-stone-700"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#E05E36]" /> {t("home.badge")}
            </motion.div>
            <h1 className="heading text-3xl sm:text-4xl md:text-5xl font-black text-stone-900 mt-3 leading-[1.05]" data-testid="home-h1">
              {t("home.h1a")}<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#E05E36] to-[#c94e2a]">{t("home.h1b")}</span>
            </h1>
            <p className="mt-3 text-stone-600 text-sm sm:text-[15px] max-w-xl leading-relaxed" data-testid="home-subtitle">
              {t("home.subtitle")}
            </p>
            <button
              onClick={() => detect()}
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900 transition-colors"
              data-testid="home-locate-btn"
              data-location-state={locState}
              title={
                locState === "live"       ? "Live GPS · tap to refresh"
              : locState === "recent"     ? `GPS · ${locAge} · tap to refresh`
              : locState === "manual"     ? "You picked this location · tap to detect"
              : locState === "last_known" ? `Last known · ${locAge} · tap to refresh`
              : locState === "cached"     ? `Stale · ${locAge} · tap to refresh`
                                          : "Default · tap to detect"
              }
            >
              <Crosshair size={12} className={locLoading ? "animate-spin" : ""} />
              {city ? city : t("home.detect")}
              {/* Freshness dot — colour reflects the 6-state location model */}
              <span className={`ml-1 inline-block w-1.5 h-1.5 rounded-full ${
                locState === "live"       ? "bg-emerald-400 animate-pulse"
              : locState === "recent"     ? "bg-emerald-400"
              : locState === "manual"     ? "bg-sky-400"
              : locState === "last_known" ? "bg-amber-400"
              : locState === "cached"     ? "bg-orange-400"
                                          : "bg-white/40"
              }`} />
              {locState !== "live" && locState !== "default" && locState !== "manual" && (
                <span className="opacity-70">· {locAge}</span>
              )}
            </button>
          </div>
          <div className="hidden sm:flex flex-col items-end gap-2 shrink-0">
            {/* Phase 4B · demoted to a secondary (outline) style so the hero
                 has ONE dominant primary CTA — the "Find" button on the
                 search bar below. This "Get started" is now the escape
                 hatch for visitors who want to sign up before searching. */}
            <Link
              to="/live-world"
              className="rounded-full bg-gradient-to-r from-[#E05E36] to-[#c94e2a] text-white text-sm font-black px-5 py-2 shadow-lg hover:shadow-xl transition-shadow inline-flex items-center gap-1.5"
              data-testid="home-liveworld-cta"
            >
              🌍 {t("home.live_world")}
            </Link>
            <Link
              to="/onboarding"
              className="rounded-full border border-stone-300 bg-stone-50 hover:bg-stone-100 text-stone-800 text-sm font-black px-5 py-2 transition-colors"
              data-testid="home-signin-cta"
            >
              {t("home.get_started")}
            </Link>
            <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">{t("home.free60")}</div>
          </div>
        </div>

        {/* Primary search — one clear entry point. AI natural-language input
            lives below (secondary), so first-time visitors aren't confused
            by two competing search bars. */}
        <form
          onSubmit={submit}
          className="mt-6 flex items-center gap-2 rounded-full bg-stone-100 backdrop-blur-md border border-stone-200 p-1.5 pl-4"
        >
          <SearchIcon size={16} className="text-stone-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("home.search_ph")}
            aria-label={t("home.search_aria")}
            className="flex-1 bg-transparent outline-none text-sm text-stone-900 placeholder:text-stone-500 py-2"
            data-testid="home-search-input"
          />
          <button
            type="submit"
            className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white text-sm font-bold px-5 py-2 transition-colors"
            data-testid="home-search-btn"
          >
            {t("home.find")}
          </button>
        </form>

        {/* Simple 3-step mental model — helps first-time visitors immediately
            understand HOW Find2Near works. Kept minimal so it doesn't compete
            with the search bar. */}
        <ol
          className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-widest text-stone-500 font-bold"
          data-testid="home-how-it-works"
        >
          <li className="inline-flex items-center gap-1.5">
            <span className="grid place-items-center w-4 h-4 rounded-full bg-[#E05E36]/80 text-white text-[9px]">1</span>
            {t("home.step1")}
          </li>
          <li className="text-stone-300">·</li>
          <li className="inline-flex items-center gap-1.5">
            <span className="grid place-items-center w-4 h-4 rounded-full bg-[#E05E36]/80 text-white text-[9px]">2</span>
            {t("home.step2")}
          </li>
          <li className="text-stone-300">·</li>
          <li className="inline-flex items-center gap-1.5">
            <span className="grid place-items-center w-4 h-4 rounded-full bg-[#E05E36]/80 text-white text-[9px]">3</span>
            {t("home.step3")}
          </li>
        </ol>

        {/* Quick examples — spoken like natural requests so it primes the
            future voice/natural-language UX without pretending it exists. */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="home-quick-chips">
          {[
            ["doctor near me", "doctor"], ["plumber", "plumber"], ["hotel for family", "hotel"],
            ["PG for rent", "pg"], ["delivery job", "delivery"], ["school for my child", "school"],
          ].map(([k, id]) => (
            <button
              key={k}
              onClick={() => navigate(`/search?q=${encodeURIComponent(k)}`)}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-100 border border-stone-200 text-stone-700 transition-colors"
              data-testid={`home-chip-${k.replace(/\s+/g, '-')}`}
            >
              {t(`home.chip.${id}`, k)}
            </button>
          ))}
        </div>

        {/* Single universal search above (→ /search → /api/search/universal). The former second
            "AI search" bar was a duplicate engine; contextual help now lives in ContextAssist. */}
        <ContextAssist />
        {loc.source !== "default" && typeof loc.lat === "number" && typeof loc.lng === "number" && (
          <NearbyHomeWidget lat={loc.lat} lng={loc.lng} />
        )}
        <Link to="/business-360" data-testid="home-b360-card"
              className="mt-3 block bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 text-white rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 grid place-items-center text-xl">📊</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest opacity-80 font-black">Business 360</div>
              <div className="font-black text-sm">Plan · Measure · Improve · Expand</div>
              <div className="text-[11px] opacity-80">OKRs, readiness radar, expansion checklists, AI insights.</div>
            </div>
            <ChevronRight size={16} className="opacity-70"/>
          </div>
        </Link>
        <Link to="/efp" data-testid="home-efp-card"
              className="mt-3 block bg-gradient-to-br from-amber-600 via-orange-600 to-rose-600 text-white rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 grid place-items-center text-xl">💠</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest opacity-90 font-black">EFP</div>
              <div className="font-black text-sm">Elite Fund Pool</div>
              <div className="text-[11px] opacity-90">Verified users · Pooled capital · Native ledger.</div>
            </div>
            <ChevronRight size={16} className="opacity-70"/>
          </div>
        </Link>
        <Link to="/aina/studio" data-testid="home-aina-card"
              className="mt-3 block bg-gradient-to-br from-fuchsia-600 via-rose-600 to-pink-600 text-white rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 grid place-items-center text-xl">✨</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest opacity-90 font-black">AINA · Business OS</div>
              <div className="font-black text-sm">Marketing Studio</div>
              <div className="text-[11px] opacity-90">Posts · Captions · Hashtags · Offers · Festival campaigns.</div>
            </div>
            <ChevronRight size={16} className="opacity-70"/>
          </div>
        </Link>
        <MyDayCard />

        {/* Live weather at the visitor's location — same chip as the map,
            but sized as a full card that leads back into /nearby. */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <WeatherCard
            lat={typeof loc.lat === "number" ? loc.lat : 19.076}
            lng={typeof loc.lng === "number" ? loc.lng : 72.8777}
            cityLabel={city}
          />
          <Link to="/places" className="block group" data-testid="home-coverage-tile-link">
            <CoverageChip variant="tile" className="h-full group-hover:brightness-110 transition-[filter]" />
          </Link>
          <div className="rounded-3xl p-4 bg-stone-50 border border-stone-200 text-stone-700 text-sm flex items-center gap-3">
            <Sparkles size={16} className="text-emerald-300 shrink-0"/>
            <div>
              <div className="font-black text-stone-900 text-[13px]">Plan any trip with confidence.</div>
              <div className="text-stone-500 text-[11px] mt-0.5">Tap the weather card to open the Personal Map — get A → B routing, corridor POIs, and rain-probability along the way.</div>
            </div>
          </div>
        </div>

        {/* Marketplace Landing Card (M-4 → M-7). Sits between the search
            surfaces and the app grid so first-time visitors see the
            "describe → get built" proposition without scrolling. */}
        <button
          type="button"
          onClick={() => navigate("/marketplace")}
          className="mt-5 group w-full text-left rounded-3xl overflow-hidden border border-[#E05E36]/40 bg-gradient-to-br from-[#E05E36] via-[#c94e2a] to-[#a83f21] p-5 sm:p-6 hover:from-[#c94e2a] hover:to-[#8f341b] transition-colors shadow-lg"
          data-testid="home-marketplace-card"
          aria-label="Open the Find2Near software marketplace"
        >
          <div className="flex items-start gap-4">
            <div className="hidden sm:grid place-items-center w-14 h-14 rounded-2xl bg-white/15 border border-white/25 shrink-0">
              <Sparkles size={22} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-white/80">New · Software marketplace</div>
              <div className="heading text-white font-black text-lg sm:text-xl mt-1 leading-tight">
                Describe what you want — we'll build it.
              </div>
              <div className="text-white/90 text-[12px] sm:text-[13px] mt-1.5 leading-snug">
                Website, mobile app, business software — tell us in plain English.
                Get an indicative price and start with escrowed payment.
              </div>
            </div>
            <div className="hidden sm:inline-flex items-center gap-1 text-white font-bold text-sm shrink-0 group-hover:translate-x-0.5 transition-transform">
              Start <ArrowRight size={16} />
            </div>
          </div>
        </button>

        {/* Icon-grid — the same visual language as the logged-in launcher */}
        {PUBLIC_GROUPS.map((g) => (
          <Section key={g.title} title={g.title}>
            {g.apps.map((a, i) => <PublicTile key={a.id} app={a} delay={i * 0.02} />)}
          </Section>
        ))}

        {/* Trending — live proof the platform is real */}
        {trending.length > 0 && (
          <Section title={`Trending${city ? ` near ${city}` : ""}`}>
            <div className="col-span-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {trending.slice(0, 6).map((l) => (
                <Link
                  key={l.listing_id}
                  to={`/listing/${l.listing_id}`}
                  className="rounded-2xl overflow-hidden bg-stone-100 backdrop-blur border border-stone-200 hover:border-[#E05E36]/60 hover:-translate-y-0.5 transition-all"
                  data-testid={`trending-${l.listing_id}`}
                >
                  <div className="aspect-[4/3] bg-stone-50 overflow-hidden">
                    {l.images?.[0] && <img loading="lazy" src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-1">
                      {l.category} <span className="mx-1">·</span> <Star size={10} className="text-[#DDA77B]" /> {l.rating_avg || "—"}
                    </div>
                    <div className="mt-1 heading font-bold text-stone-900 line-clamp-1">{l.title}</div>
                    <div className="text-xs text-stone-500 line-clamp-1">{l.city}</div>
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* Sign-in nudge — appears once the visitor has scrolled through everything */}
        <div className="mt-14 rounded-3xl bg-gradient-to-br from-[#E05E36] to-[#c94e2a] p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-52 h-52 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/80 font-black">{t("home.cta_kicker")}</div>
              <h3 className="heading text-2xl md:text-3xl font-black text-white mt-1">{t("home.cta_title")}</h3>
              <p className="text-white/90 text-sm mt-1 max-w-md">
                Sign in — it's free and takes about 60 seconds — to save what you found, message providers directly, and post your own listings.
              </p>
            </div>
            <Link
              to="/onboarding"
              className="rounded-full bg-white text-[#E05E36] font-black px-6 py-3 inline-flex items-center gap-2 hover:scale-105 transition-transform shadow-xl"
              data-testid="home-signin-bottom"
            >
              Get started <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {/* Slim footer */}
      <footer className="relative border-t border-stone-200 py-8 text-stone-500 text-xs" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-5 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} find2near · founded by <Link to="/about" className="text-[#E05E36] font-semibold hover:underline" data-testid="footer-founder">Pushkar Agarwal</Link></div>
          <div className="flex flex-wrap gap-4">
            <Link to="/about"   className="hover:text-stone-900" data-testid="footer-about">{t("shell.about")}</Link>
            <Link to="/contact" className="hover:text-stone-900" data-testid="footer-contact">{t("shell.contact")}</Link>
            <Link to="/terms"   className="hover:text-stone-900" data-testid="footer-terms">{t("shell.terms")}</Link>
            <Link to="/privacy" className="hover:text-stone-900" data-testid="footer-privacy">{t("shell.privacy")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Small section header + tile grid — kept in-file since it's home-only styling.
function Section({ title, children }) {
  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[11px] uppercase tracking-[0.25em] font-black text-stone-500">{title}</h2>
        <div className="h-px flex-1 mx-4 bg-gradient-to-r from-stone-300 to-transparent" />
      </div>
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 md:gap-5">
        {children}
      </div>
    </div>
  );
}

function PublicTile({ app, delay = 0 }) {
  const Icon = iconByName(app.icon);
  const appIconName = canonicalIconName(app.id);
  return (
    <Link
      to={app.to}
      className="group flex flex-col items-center gap-2 text-stone-800 hover:text-stone-900 transition-colors"
      data-testid={`home-tile-${app.id}`}
    >
      <motion.div
        initial={{ opacity: 0, y: 6, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay, duration: 0.28, ease: "easeOut" }}
        whileHover={{ y: -4, scale: 1.04 }}
        whileTap={{ scale: 0.94 }}
        className={
          appIconName
            ? "w-16 h-16 md:w-[68px] md:h-[68px] grid place-items-center relative"
            : `w-16 h-16 md:w-[68px] md:h-[68px] rounded-[22px] bg-gradient-to-br ${app.grad} shadow-[0_10px_30px_-8px_rgba(0,0,0,0.55)] ring-1 ring-white/10 grid place-items-center relative overflow-hidden`
        }
      >
        {appIconName ? (
          <AppIcon name={appIconName} size={68} alt={app.label} />
        ) : (
          <>
            <span className="absolute inset-x-2 top-1 h-4 rounded-full bg-white/25 blur-md opacity-60 group-hover:opacity-90 transition-opacity" />
            <Icon size={28} className="relative text-white drop-shadow" strokeWidth={2.25} />
          </>
        )}
      </motion.div>
      <div className="text-[11px] md:text-xs font-bold text-center leading-tight max-w-[80px] truncate">{app.label}</div>
    </Link>
  );
}
